import React, { useState, useMemo, useCallback, useEffect } from "react";
import { useQuery, useQueries } from "@tanstack/react-query";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Input } from "@/components/ui/input";
import { Loader2, ChevronRight, ChevronDown, X, Search } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { CPVRecommendations } from "./CPVRecommendations";
import { CPVTreeNode, fetchCPVDivisions, fetchCPVTreeNodesByParent, fetchCPVNodeByCode } from "@/utils/cpvTreeUtils";
import { supabase } from "@/integrations/supabase/client";

interface CPVSectionFilterProps {
  selectedCPVs: string[];
  onCPVChange: (cpv: string) => void;
}

export const CPVSectionFilter = ({ selectedCPVs, onCPVChange }: CPVSectionFilterProps) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [expandedNodes, setExpandedNodes] = useState<Record<string, boolean>>({});
  const [lastSelectedCPV, setLastSelectedCPV] = useState<string>("");
  const [searchResults, setSearchResults] = useState<CPVTreeNode[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  const { data: divisions = [], isLoading: isLoadingDivisions } = useQuery({
    queryKey: ["cpv-divisions"],
    queryFn: fetchCPVDivisions,
    staleTime: 5 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  const expandedNodeKeys = useMemo(() => {
    return Object.keys(expandedNodes).filter(key => expandedNodes[key] === true);
  }, [expandedNodes]);
  
  const childQueriesResults = useQueries({
    queries: expandedNodeKeys.map(nodeCode => ({
      queryKey: ["cpv-children", nodeCode],
      queryFn: () => fetchCPVTreeNodesByParent(nodeCode),
      enabled: !!nodeCode && expandedNodes[nodeCode] === true,
      staleTime: 5 * 60 * 1000,
      gcTime: 30 * 60 * 1000,
    }))
  });

  const childQueries = useMemo(() => {
    return expandedNodeKeys.map((nodeCode, index) => ({
      nodeCode,
      data: childQueriesResults[index]?.data || [],
    }));
  }, [expandedNodeKeys, childQueriesResults]);
  
  const childrenMap = useMemo(() => {
    const map: Record<string, CPVTreeNode[]> = {};
    if (Array.isArray(childQueries)) {
      childQueries.forEach(query => {
        if (query && query.nodeCode) {
          map[query.nodeCode] = Array.isArray(query.data) ? query.data : [];
        }
      });
    }
    return map;
  }, [childQueries]);

  useEffect(() => {
    if (searchTerm && searchTerm.length >= 2) {
      performSearch();
    } else {
      setSearchResults([]);
    }
  }, [searchTerm]);

  const performSearch = async () => {
    if (!searchTerm || searchTerm.length < 2) return;
    
    setIsSearching(true);
    try {
      // Search directly in the cpv_tree table
      const { data: treeResults, error: treeError } = await supabase
        .from("cpv_tree")
        .select("*")
        .ilike("beskrivelse", `%${searchTerm}%`)
        .limit(50);
      
      if (treeError) throw treeError;

      // Also search in the CPV Table for more results
      const { data: tableResults, error: tableError } = await supabase
        .from("CPV Table")
        .select("CPV, Beskrivelse")
        .ilike("Beskrivelse", `%${searchTerm}%`)
        .limit(50);
      
      if (tableError) throw tableError;
      
      // Convert and merge results
      const treeNodes = (treeResults || []).map(node => ({
        cpv_code: node.cpv_code.toString(),
        beskrivelse: node.beskrivelse,
        level: node.level as 'Division' | 'Group' | 'Class' | 'Category' | 'Subcategory',
        parent_code: node.parent_code ? node.parent_code.toString() : undefined,
        isExpanded: false
      }));
      
      const tableNodes = (tableResults || []).map(node => ({
        cpv_code: node.CPV.toString(),
        beskrivelse: node.Beskrivelse,
        level: 'Category' as 'Division' | 'Group' | 'Class' | 'Category' | 'Subcategory',
        isExpanded: false
      }));
      
      // Deduplicate by cpv_code
      const allResults = [...treeNodes, ...tableNodes];
      const uniqueResults = Array.from(
        new Map(allResults.map(node => [node.cpv_code, node])).values()
      );
      
      setSearchResults(uniqueResults);
    } catch (error) {
      console.error("Error searching CPV codes:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const toggleNodeExpansion = useCallback((e: React.MouseEvent, nodeCode: string) => {
    e.preventDefault();
    e.stopPropagation();
    
    setExpandedNodes(prev => ({
      ...prev,
      [nodeCode]: !prev[nodeCode]
    }));
  }, []);

  const handleCPVChange = useCallback((cpv: string) => {
    setLastSelectedCPV(cpv);
    onCPVChange(cpv);
  }, [onCPVChange]);

  const filterNode = useCallback((node: CPVTreeNode): boolean => {
    if (!searchTerm) return true;
    return node.beskrivelse.toLowerCase().includes(searchTerm.toLowerCase());
  }, [searchTerm]);

  const { data: selectedCPVNodes = {}, isLoading: isLoadingDescriptions } = useQuery({
    queryKey: ["cpv-descriptions", selectedCPVs],
    queryFn: async () => {
      if (!selectedCPVs || selectedCPVs.length === 0) return {};
      
      const result: Record<string, string> = {};
      
      const { data: cpvTreeData, error: cpvTreeError } = await supabase
        .from("cpv_tree")
        .select("cpv_code, beskrivelse")
        .in("cpv_code", selectedCPVs.map(code => parseInt(code)));
      
      if (cpvTreeError) {
        console.error("Error fetching CPV descriptions from cpv_tree:", cpvTreeError);
      } else if (cpvTreeData) {
        cpvTreeData.forEach(item => {
          result[item.cpv_code.toString()] = item.beskrivelse;
        });
      }
      
      const missingCodes = selectedCPVs.filter(code => !result[code]);
      if (missingCodes.length > 0) {
        const { data: cpvTableData, error: cpvTableError } = await supabase
          .from("CPV Table")
          .select("CPV, Beskrivelse")
          .in("CPV", missingCodes.map(code => parseInt(code)));
          
        if (cpvTableError) {
          console.error("Error fetching CPV descriptions from CPV Table:", cpvTableError);
        } else if (cpvTableData) {
          cpvTableData.forEach(item => {
            result[item.CPV.toString()] = item.Beskrivelse;
          });
        }
      }
      
      const stillMissingCodes = selectedCPVs.filter(code => !result[code]);
      if (stillMissingCodes.length > 0) {
        await Promise.all(
          stillMissingCodes.map(async (code) => {
            try {
              const node = await fetchCPVNodeByCode(code);
              if (node && node.beskrivelse) {
                result[code] = node.beskrivelse;
              } else {
                result[code] = `CPV ${code}`;
              }
            } catch (err) {
              console.error(`Error fetching description for CPV ${code}:`, err);
              result[code] = `CPV ${code}`;
            }
          })
        );
      }
      
      return result;
    },
    enabled: selectedCPVs.length > 0,
    staleTime: 5 * 60 * 1000,
  });

  const renderTreeNode = useCallback((node: CPVTreeNode) => {
    if (!node) return null;

    const isExpanded = expandedNodes[node.cpv_code] === true;
    const hasChildren = node.level !== 'Subcategory';
    const showExpander = hasChildren;
    const isSelected = selectedCPVs?.includes(node.cpv_code);
    
    const children = isExpanded ? childrenMap[node.cpv_code] || [] : [];

    if (searchTerm && !filterNode(node)) return null;

    return (
      <div key={node.cpv_code} className="tree-node">
        <div className="flex items-start space-x-2 hover:bg-gray-50 p-2 rounded-md group">
          {showExpander ? (
            <button
              onClick={(e) => toggleNodeExpansion(e, node.cpv_code)}
              className="w-5 h-5 mt-0.5 flex items-center justify-center text-gray-500 hover:text-primary"
            >
              {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          ) : (
            <div className="w-5 h-5" />
          )}
          
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => handleCPVChange(node.cpv_code)}
            id={`cpv-${node.cpv_code}`}
            className="mt-1"
          />
          
          <label
            htmlFor={`cpv-${node.cpv_code}`}
            className="text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex-1"
          >
            <div className="text-gray-800 font-medium">{node.beskrivelse}</div>
          </label>
        </div>
        
        {isExpanded && (
          <div className="ml-7 border-l pl-4 mt-1">
            {Array.isArray(children) && children.length > 0 ? 
              children.map(childNode => renderTreeNode(childNode)) : 
              <div className="text-sm text-gray-500 py-2">No subcategories</div>
            }
          </div>
        )}
      </div>
    );
  }, [expandedNodes, selectedCPVs, searchTerm, filterNode, handleCPVChange, toggleNodeExpansion, childrenMap]);

  const renderSearchResults = () => {
    if (isSearching) {
      return (
        <div className="flex justify-center py-4">
          <Loader2 className="h-5 w-5 animate-spin text-primary mr-2" />
          <span className="text-sm text-gray-600">Searching...</span>
        </div>
      );
    }

    if (!searchResults.length) {
      return null;
    }

    return (
      <div className="space-y-2">
        <h3 className="text-sm font-semibold mb-2 flex items-center">
          <Search className="h-4 w-4 mr-1 text-primary" />
          Search Results
        </h3>
        <div className="space-y-1 max-h-[200px] overflow-y-auto">
          {searchResults.map(result => (
            <div
              key={result.cpv_code}
              className={`flex items-center space-x-2 p-2 hover:bg-gray-50 rounded-md ${
                selectedCPVs?.includes(result.cpv_code) ? "bg-accent/30" : ""
              }`}
            >
              <Checkbox
                checked={selectedCPVs?.includes(result.cpv_code)}
                onCheckedChange={() => handleCPVChange(result.cpv_code)}
                id={`search-cpv-${result.cpv_code}`}
              />
              <label
                htmlFor={`search-cpv-${result.cpv_code}`}
                className="text-sm cursor-pointer flex-1"
              >
                {result.beskrivelse}
                <span className="text-xs ml-2 text-gray-500">({result.cpv_code})</span>
              </label>
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (isLoadingDivisions) {
    return (
      <div className="space-y-4">
        <div className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <span className="ml-2 text-sm text-gray-600">Loading CPV hierarchy...</span>
        </div>
      </div>
    );
  }

  const getCPVDescription = (cpvCode: string) => {
    if (isLoadingDescriptions) {
      return <span className="flex items-center"><Loader2 className="h-3 w-3 mr-1 animate-spin" /> Loading...</span>;
    }
    
    return selectedCPVNodes[cpvCode] || `CPV ${cpvCode}`;
  };

  return (
    <div className="space-y-4">
      <Input
        type="search"
        placeholder="Search CPV descriptions..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        className="max-w-md"
      />
      
      {renderSearchResults()}
      
      {!searchTerm && (
        <ScrollArea className="h-[400px] rounded-md border p-4">
          {divisions && Array.isArray(divisions) && divisions.length > 0 ? (
            divisions.map(division => renderTreeNode(division))
          ) : (
            <div className="text-center py-4 text-gray-500">No CPV divisions found</div>
          )}
        </ScrollArea>
      )}

      {lastSelectedCPV && (
        <CPVRecommendations
          selectedCPV={lastSelectedCPV}
          onCPVSelect={handleCPVChange}
          existingSelections={selectedCPVs || []}
        />
      )}

      {selectedCPVs && selectedCPVs.length > 0 && (
        <div className="flex flex-wrap gap-2 border-t pt-4">
          <p className="text-sm text-muted-foreground w-full mb-2">
            Selected industries:
          </p>
          {selectedCPVs.map((cpvCode) => (
            <Badge
              key={cpvCode}
              variant="secondary"
              className="px-3 py-1 cursor-pointer hover:bg-secondary/80 group"
              onClick={() => handleCPVChange(cpvCode)}
            >
              {getCPVDescription(cpvCode)}
              <X className="w-3 h-3 ml-2 inline-block opacity-0 group-hover:opacity-100 transition-opacity" />
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
};
